import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

const selectableSurfaceVariants = cva(
  'min-w-0 cursor-pointer border border-control-border bg-background text-foreground outline-none transition-[background-color,border-color,box-shadow,color] hover:border-selection-border hover:bg-muted/30 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:opacity-65 data-[state=on]:border-selection-border data-[state=on]:bg-accentSecondary data-[state=on]:text-accentSecondary-foreground data-[state=on]:ring-1 data-[state=on]:ring-inset data-[state=on]:ring-selection-border',
  {
    variants: {
      appearance: {
        default: '',
        bare: 'border-transparent bg-transparent hover:border-transparent data-[state=on]:border-transparent data-[state=on]:ring-0',
      },
      radius: {
        control: 'rounded-md',
        surface: 'rounded-xl',
      },
      padding: {
        none: 'p-0',
        sm: 'p-2',
        compact: 'p-3',
        default: 'p-4',
        lg: 'p-6',
      },
    },
    defaultVariants: {
      appearance: 'default',
      radius: 'control',
      padding: 'default',
    },
  }
)

export type SelectableSurfaceProps = ComponentProps<'button'> &
  VariantProps<typeof selectableSurfaceVariants> & {
    selected?: boolean
  }

export type RadioSurfaceProps = SelectableSurfaceProps

function SelectableSurface({
  selected = false,
  appearance = 'default',
  radius = 'control',
  padding = 'default',
  className,
  type = 'button',
  ...props
}: SelectableSurfaceProps) {
  return (
    <button
      {...props}
      type={type}
      data-slot="selectable-surface"
      data-state={selected ? 'on' : 'off'}
      aria-pressed={selected}
      className={cn(selectableSurfaceVariants({ appearance, radius, padding }), className)}
    />
  )
}

function RadioSurface({
  selected = false,
  appearance = 'default',
  radius = 'control',
  padding = 'default',
  className,
  type = 'button',
  ...props
}: RadioSurfaceProps) {
  return (
    <button
      {...props}
      type={type}
      role="radio"
      data-slot="radio-surface"
      data-state={selected ? 'on' : 'off'}
      aria-checked={selected}
      className={cn(selectableSurfaceVariants({ appearance, radius, padding }), className)}
    />
  )
}

export { RadioSurface, SelectableSurface, selectableSurfaceVariants }
